import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type {
  ManufactureCMOInfo,
  PackageCMOInfo,
  PreparationResponse,
} from '@/entities/cmo-record/model/types';
import { Input } from '@/shared/ui';
import { Checkbox } from '@/shared/ui/checkbox';
import { Text } from '@/shared/ui/text';

const isImageUrl = (value: string | null): boolean => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 's3:';
  } catch {
    return false;
  }
};

export function useCMOTable(preparationData: PreparationResponse | undefined) {
  // 쿼리 데이터를 deep copy해서 로컬 상태로 관리
  const [localData, setLocalData] = useState<(ManufactureCMOInfo | PackageCMOInfo)[]>([]);

  // 쿼리 데이터가 변경되면 로컬 상태 업데이트 (deep copy)
  useEffect(() => {
    if (preparationData?.data_list) {
      setLocalData(structuredClone(preparationData.data_list));
    }
  }, [preparationData]);

  // revision_no 변경 핸들러
  const handleRevisionNumberChange = (documentNo: string, newValue: string) => {
    setLocalData((prev) =>
      prev.map((item) =>
        item.document_no === documentNo ? { ...item, revision_no: newValue } : item
      )
    );
  };

  // 제조 기록서 컬럼
  const manufacturingColumns = useMemo<ColumnDef<ManufactureCMOInfo>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            aria-label="전체 선택"
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label="행 선택"
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'product_name',
        header: '제품명',
      },
      {
        accessorKey: 'main_code',
        header: '대표코드',
      },
      {
        accessorKey: 'document_no',
        header: '문서번호',
      },
      {
        accessorKey: 'revision_no',
        header: '개정번호',
        cell: ({ row }) => (
          <Input
            className="w-24"
            onChange={(e) => {
              handleRevisionNumberChange(row.original.document_no, e.target.value);
            }}
            size="xs"
            value={row.original.revision_no}
          />
        ),
      },
      {
        accessorKey: 'product_standard_no',
        header: '제품표준서 번호',
      },
      {
        accessorKey: 'pill_marking_one',
        header: '낱알 식별표시',
        cell: ({ row }) => {
          const pillMarkingOne = row.getValue('pill_marking_one');
          if (isImageUrl(pillMarkingOne as string)) {
            return (
              <div className="relative w-20 h-20">
                <img
                  alt="각인(앞)"
                  className="object-contain w-full h-full"
                  src={pillMarkingOne as string}
                />
              </div>
            );
          }

          return <Text typography="typo-text-xl">{pillMarkingOne as string}</Text>;
        },
      },
      {
        accessorKey: 'pill_marking_two',
        header: '',
        cell: ({ row }) => {
          const pillMarkingTwo = row.getValue('pill_marking_two');

          if (isImageUrl(pillMarkingTwo as string)) {
            return (
              <div className="relative w-20 h-20">
                <img
                  alt="각인(뒤)"
                  className="object-contain w-full h-full"
                  src={pillMarkingTwo as string}
                />
              </div>
            );
          }

          return <Text typography="typo-text-xl">{pillMarkingTwo as string}</Text>;
        },
      },
    ],
    []
  );

  // 포장 기록서 컬럼
  const packagingColumns = useMemo<ColumnDef<PackageCMOInfo>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            aria-label="전체 선택"
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label="행 선택"
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'product_company_name',
        header: '제품명/회사명',
      },
      {
        accessorKey: 'product_name',
        header: '제품명',
      },
      {
        accessorKey: 'main_code',
        header: '대표코드',
      },
      {
        accessorKey: 'product_code',
        header: '제품코드',
      },
      {
        accessorKey: 'document_no',
        header: '문서번호',
      },
      {
        accessorKey: 'revision_no',
        header: '개정번호',
        cell: ({ row }) => (
          <Input
            className="w-24"
            onChange={(e) => {
              handleRevisionNumberChange(row.original.document_no, e.target.value);
            }}
            size="xs"
            value={row.original.revision_no}
          />
        ),
      },
      {
        accessorKey: 'product_standard_no',
        header: '제품표준서 번호',
      },
      {
        accessorKey: 'code',
        header: '코드',
      },
      {
        accessorKey: 'pill_marking_one',
        header: '낱알 식별표시',
        cell: ({ row }) => {
          const pillMarkingOne = row.getValue('pill_marking_one');
          if (isImageUrl(pillMarkingOne as string)) {
            return (
              <div className="relative w-20 h-20">
                <img
                  alt="각인(앞)"
                  className="object-contain w-full h-full"
                  src={pillMarkingOne as string}
                />
              </div>
            );
          }

          return <Text typography="typo-text-xl">{(pillMarkingOne as string) || '-'}</Text>;
        },
      },
      {
        accessorKey: 'pill_marking_two',
        header: '',
        cell: ({ row }) => {
          const pillMarkingTwo = row.getValue('pill_marking_two');

          if (isImageUrl(pillMarkingTwo as string)) {
            return (
              <div className="relative w-20 h-20">
                <img
                  alt="각인(뒤)"
                  className="object-contain w-full h-full"
                  src={pillMarkingTwo as string}
                />
              </div>
            );
          }

          return <Text typography="typo-text-xl">{pillMarkingTwo as string}</Text>;
        },
      },
      {
        accessorKey: 'bar_code',
        header: '바코드',
      },
      {
        accessorKey: 'standard_code',
        header: '표준코드',
      },
    ],
    []
  );

  return {
    localData,
    manufacturingColumns,
    packagingColumns,
    setLocalData,
  };
}
